import type { OptimizelyDatafile } from '#types/p13n'
import { getOptimizelyAttributes } from '#core/utils/p13n'
import { createInstance, OptimizelyDecideOption } from '@optimizely/optimizely-sdk'

let optimizelyClient: ReturnType<typeof createInstance> | null = null // Singleton instance

export const useOptimizely = async () => {
  const { configP13n: { optimizelySdkKey, enableOptimizely } } = useFeatureFlags()
  const auth = useAuthStore()
  const cart = useCartStore()
  const userStore = useUserStore()
  const route = useRoute()
  const region = useRegion()
  const { width, height } = useWindowSize()
  const optaNonConsentCookie = useCookie('OptanonConsent')
  const latLong = useCookie('Edgescape-Lat-Long').value
  const datafileUrl = enableOptimizely ? `https://cdn.optimizely.com/datafiles/${optimizelySdkKey}.json` : ''

  const { data: datafile } = await useAsyncData(
    'optimizely-datafile',
    () => $fetch(datafileUrl),
    {
      server: true,
      lazy: false,
      default: () => null,
    }
  )

  if (enableOptimizely && !optimizelyClient && datafile.value) {
    optimizelyClient = createInstance({
      datafile: datafile.value as OptimizelyDatafile
    })
  }

  const defineUser = () => {
    const attributes = getOptimizelyAttributes(
      {
        route,
        cart,
        auth,
        userStore,
        windowSize: { width: width.value, height: height.value },
        latLong
      }
    )
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'user',
        attributes
      })
    }
    console.info('Optimizely attributes:', attributes)
    return optimizelyClient?.createUserContext(auth.consumerId as string, attributes)
  }

  const getTargetingConsent = (groups) => {
    const consent = checkConsentGroup(region === 'EMEA', route.query.preview === 'true', decodeURIComponent(optaNonConsentCookie.value || ''))
    return groups.every((key) => consent[key])
  }

  const decide = (key: string) => {
    if (!enableOptimizely) return
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    return user?.decide(key, [
      OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
      OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE,
    ])
  }

  const trackEvent = (eventKey: string, properties, revenue?) => {
    if (!enableOptimizely) return
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    const tags = {
      $opt_event_properties: properties,
      ...(revenue && { revenue })
    }
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'event',
        eventName: eventKey,
        tags: {
          ...(revenue && { revenue })
        },
        properties
      })
    }
    console.info(`Tracking ${eventKey} Event for Optimizely: tags were`, tags)
    user?.trackEvent(eventKey, tags)
  }

  return {
    optimizelyClient,
    decide,
    trackEvent
  }
}
